import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Select,
  Input,
  Button,
  Text,
  Checkbox,
  useToast,
} from '@chakra-ui/react';
import { createAlarm, updateAlarm } from '../api/alarm';

const CreateAlarmModalComponent = ({
  isOpen,
  onClose,
  alarmGroups = [],
  newAlarm,
  setNewAlarm,
  refetchAlarms,
  isEditMode = false,
  selectedAlarm = {},
}) => {
  const toast = useToast();

  useEffect(() => {
    if (isEditMode && selectedAlarm) {
      const isDisabled =
        parseInt(selectedAlarm.operand) === 1 ||
        parseInt(selectedAlarm.operand) === 6;

      setNewAlarm({
        enable: selectedAlarm.enable,
        name: selectedAlarm.name,
        code: selectedAlarm.code,
        operand: selectedAlarm.operand,
        delay: isDisabled ? '' : selectedAlarm.delay,
        threshold: isDisabled ? '' : selectedAlarm.threshold,
        group: selectedAlarm.group,
      });
    }
  }, [isEditMode, selectedAlarm, setNewAlarm]);

  const handleSave = async () => {
    try {
      const alarmToSave = {
        enable: newAlarm.enable,
        name: newAlarm.name,
        operand: newAlarm.operand,
        group: newAlarm.group,
        code: newAlarm.code,
        threshold: newAlarm.threshold,
        delay: newAlarm.delay,
      };

      if (
        !alarmToSave.name ||
        !alarmToSave.operand ||
        !alarmToSave.group ||
        !alarmToSave.code ||
        (isDelayAndThresholdDisabled ? false : !alarmToSave.threshold)
      ) {
        toast({
          title: 'Incomplete Data',
          description: 'Please complete all required fields.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (isEditMode) {
        await updateAlarm(selectedAlarm.id, alarmToSave);
        toast({
          title: 'Alarm Updated',
          description: 'The alarm has been successfully updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await createAlarm(alarmToSave);
        toast({
          title: 'Alarm Created',
          description: 'The alarm has been successfully created.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      onClose();
      refetchAlarms();
    } catch (error) {
      toast({
        title: `Failed to ${isEditMode ? 'update' : 'create'} alarm`,
        description: error.toString(),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleNewAlarmChange = e => {
    const { name, value, type, checked } = e.target;

    if (name === 'operand') {
      if (value === '1' || value === '6') {
        setNewAlarm(prev => ({
          ...prev,
          operand: value,
          delay: '',
          threshold: '',
        }));
      } else {
        setNewAlarm(prev => ({
          ...prev,
          operand: value,
        }));
      }
    } else {
      setNewAlarm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  // Operand değerine göre `delay` ve `threshold` alanlarını disable et
  const isDelayAndThresholdDisabled =
    newAlarm.operand === '1' ||
    newAlarm.operand === '6' ||
    newAlarm.operand === 6 ||
    newAlarm.operand === 1;

  const operandOptions = [
    { id: 1, label: '==' },
    { id: 2, label: '>=' },
    { id: 3, label: '=<' },
    { id: 4, label: '>' },
    { id: 5, label: '<' },
    { id: 6, label: '!=' },
    { id: 7, label: 'in range' },
    { id: 8, label: 'out of range' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Edit Alarm' : 'Create Alarm'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap="4">
            <Flex align="center">
              <Checkbox
                isChecked={newAlarm.enable || false}
                name="enable"
                onChange={handleNewAlarmChange}
              >
                Enable Alarm
              </Checkbox>
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Name:</Text>
              <Input
                name="name"
                value={newAlarm.name || ''}
                onChange={handleNewAlarmChange}
                placeholder="Alarm Name"
              />
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Code:</Text>
              <Input
                name="code"
                value={newAlarm.code || ''}
                onChange={handleNewAlarmChange}
                placeholder="Alarm Code"
              />
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Operand:</Text>
              <Select
                name="operand"
                value={newAlarm.operand?.toString() || ''} // Burada toString() ile string'e çeviriyoruz
                onChange={handleNewAlarmChange}
              >
                {operandOptions.map(option => (
                  <option key={option.id} value={option.id.toString()}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Delay:</Text>
              <Select
                name="delay"
                value={newAlarm.delay || ''}
                onChange={handleNewAlarmChange}
                isDisabled={isDelayAndThresholdDisabled}
              >
                <option value="">None</option>
                {[...Array(21).keys()].map(value => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Threshold:</Text>
              <Input
                name="threshold"
                value={newAlarm.threshold || ''}
                onChange={handleNewAlarmChange}
                placeholder="Threshold Value"
                isDisabled={isDelayAndThresholdDisabled}
              />
            </Flex>
            <Flex align="center">
              <Text minWidth="120px">Alarm Group:</Text>
              <Select
                name="group"
                value={newAlarm.group || ''}
                onChange={handleNewAlarmChange}
                placeholder="Select Alarm Group"
              >
                {alarmGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="green" onClick={handleSave}>
            Save
          </Button>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateAlarmModalComponent;
